import React, { useState, useEffect } from 'react';
import api from '../api';
import { Plus, CheckCircle, XCircle, PencilSimple, Trash, Star, CurrencyDollar } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import PrestadorFinanceiro from './PrestadorFinanceiro';
import DocumentUploader from './DocumentUploader';

const ESCOLARIDADES = [
  'Ensino Fundamental',
  'Ensino Médio',
  'Técnico em Eletrotécnica',
  'Técnico em Eletrônica',
  'Superior em Engenharia Elétrica',
  'Superior em Engenharia Eletrônica',
  'Pós-graduação'
];

const HORARIOS = [
  'Manhã (6h-12h)',
  'Tarde (12h-18h)',
  'Noite (18h-22h)',
  'Integral',
  'Plantão 24h'
];

const ESPECIALIDADES = [
  'Instalações Residenciais',
  'Instalações Comerciais',
  'Instalações Industriais',
  'Manutenção Preventiva',
  'Manutenção Corretiva',
  'Automação',
  'Painéis Elétricos',
  'Alta Tensão',
  'Baixa Tensão',
  'SPDA (Para-raios)',
  'Ar Condicionado',
  'Energia Solar'
];

const PrestadorManagement = () => {
  const [prestadores, setPrestadores] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCredentialsDialogOpen, setIsCredentialsDialogOpen] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [selectedEspecialidades, setSelectedEspecialidades] = useState([]);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedPrestador, setSelectedPrestador] = useState(null);
  const [editData, setEditData] = useState({});
  const [isFinanceiroOpen, setIsFinanceiroOpen] = useState(false);
  const [financeiroPrestador, setFinanceiroPrestador] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    cpf: '',
    rg: '',
    email: '',
    cep: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    telefone_fixo: '',
    telefone_celular: '',
    escolaridade: '',
    horario_disponivel: '',
    valor_hora: '',
    taxa_deslocamento: '',
    senha_inicial: '',
    nivel_tecnico: 3
  });

  useEffect(() => {
    fetchPrestadores();
  }, []);

  const fetchPrestadores = async () => {
    try {
      const response = await api.get('/prestadores');
      setPrestadores(response.data);
    } catch (error) {
      console.error('Error fetching prestadores:', error);
    }
  };

  const buscarCEP = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
        const data = await response.json();
        if (!data.erro) {
          setFormData(prev => ({
            ...prev,
            endereco: data.logradouro,
            bairro: data.bairro,
            cidade: data.localidade,
            estado: data.uf
          }));
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };

  const formatCPF = (value) => {
    const cpf = value.replace(/\D/g, '');
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatCEP = (value) => {
    const cep = value.replace(/\D/g, '');
    return cep.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const formatPhone = (value) => {
    const phone = value.replace(/\D/g, '');
    if (phone.length <= 10) {
      return phone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const toggleEspecialidade = (esp) => {
    if (selectedEspecialidades.includes(esp)) {
      setSelectedEspecialidades(selectedEspecialidades.filter(e => e !== esp));
    } else {
      setSelectedEspecialidades([...selectedEspecialidades, esp]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedEspecialidades.length === 0) {
      alert('Selecione pelo menos uma especialidade');
      return;
    }

    try {
      const response = await api.post('/prestadores', {
        ...formData,
        especialidades: selectedEspecialidades,
        valor_hora: parseFloat(formData.valor_hora),
        taxa_deslocamento: parseFloat(formData.taxa_deslocamento),
        nivel_tecnico: formData.nivel_tecnico
      });
      
      setIsDialogOpen(false);
      setCreatedCredentials({
        email: formData.email,
        senha: response.data.senha_criada || formData.senha_inicial || 'Toolserv2026!',
        nome: formData.name
      });
      setIsCredentialsDialogOpen(true);
      setFormData({
        name: '',
        cpf: '',
        rg: '',
        email: '',
        cep: '',
        endereco: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        telefone_fixo: '',
        telefone_celular: '',
        escolaridade: '',
        horario_disponivel: '',
        valor_hora: '',
        taxa_deslocamento: '',
        senha_inicial: '',
        nivel_tecnico: 3
      });
      setSelectedEspecialidades([]);
      fetchPrestadores();
    } catch (error) {
      console.error('Error creating prestador:', error);
      alert('Erro ao criar prestador: ' + (error.response?.data?.detail || error.message));
    }
  };

  const StarRating = ({ value, onChange, readOnly = false, size = 16 }) => (
    <div className="flex gap-1">
      {[1,2,3,4,5].map(star => (
        <button
          key={star}
          type="button"
          onClick={() => !readOnly && onChange && onChange(star)}
          className={`${readOnly ? '' : 'cursor-pointer hover:scale-110'} transition-transform`}
          disabled={readOnly}
        >
          <Star size={size} weight={star <= value ? 'fill' : 'regular'} className={star <= value ? 'text-[#FFB800]' : 'text-zinc-600'} />
        </button>
      ))}
    </div>
  );

  const openEditPrestador = (prest) => {
    setSelectedPrestador(prest);
    setEditData({
      name: prest.name || '',
      cpf: prest.cpf || '',
      rg: prest.rg || '',
      email: prest.email || '',
      cep: prest.cep || '',
      endereco: prest.endereco || '',
      numero: prest.numero || '',
      complemento: prest.complemento || '',
      bairro: prest.bairro || '',
      cidade: prest.cidade || '',
      estado: prest.estado || '',
      telefone_fixo: prest.telefone_fixo || '',
      telefone_celular: prest.telefone_celular || '',
      escolaridade: prest.escolaridade || '',
      horario_disponivel: prest.horario_disponivel || '',
      especialidades: prest.especialidades || [],
      valor_hora: String(prest.valor_hora || ''),
      taxa_deslocamento: String(prest.taxa_deslocamento || ''),
      nivel_tecnico: prest.nivel_tecnico || 1
    });
    setIsEditOpen(true);
  };

  const handleEditPrestador = async (e) => {
    e.preventDefault();
    if (!selectedPrestador) return;
    try {
      const payload = {
        name: editData.name,
        cpf: editData.cpf,
        rg: editData.rg,
        cep: editData.cep,
        endereco: editData.endereco,
        numero: editData.numero,
        complemento: editData.complemento || null,
        bairro: editData.bairro,
        cidade: editData.cidade,
        estado: editData.estado,
        telefone_fixo: editData.telefone_fixo || null,
        telefone_celular: editData.telefone_celular,
        escolaridade: editData.escolaridade,
        horario_disponivel: editData.horario_disponivel,
        especialidades: editData.especialidades,
        valor_hora: parseFloat(editData.valor_hora),
        taxa_deslocamento: parseFloat(editData.taxa_deslocamento),
        nivel_tecnico: editData.nivel_tecnico
      };
      await api.put(`/prestadores/${selectedPrestador.id}`, payload);
      setIsEditOpen(false);
      setSelectedPrestador(null);
      fetchPrestadores();
    } catch (error) {
      alert('Erro ao editar prestador: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeletePrestador = async () => {
    if (!selectedPrestador) return;
    try {
      await api.delete(`/prestadores/${selectedPrestador.id}`);
      setIsDeleteOpen(false);
      setSelectedPrestador(null);
      fetchPrestadores();
    } catch (error) {
      alert('Erro ao excluir prestador: ' + (error.response?.data?.detail || error.message));
    }
  };

  const updateStatus = async (prestadorId, newStatus) => {
    try {
      await api.patch(`/prestadores/${prestadorId}/status?status=${newStatus}`);
      fetchPrestadores();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
          Gerenciar Prestadores
        </h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#FFB800] text-black hover:bg-[#E6A600]" data-testid="add-prestador-button">
              <Plus size={20} className="mr-2" />
              Novo Prestador
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Barlow Condensed' }}>Cadastrar Novo Prestador</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Dados Pessoais */}
              <div className="border-b border-[#27272A] pb-4">
                <h3 className="text-[#FFB800] font-semibold mb-3">Dados Pessoais</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Nome Completo *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      required
                      data-testid="prestador-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label>CPF *</Label>
                    <Input
                      value={formData.cpf}
                      onChange={(e) => setFormData({...formData, cpf: formatCPF(e.target.value)})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      placeholder="000.000.000-00"
                      maxLength={14}
                      required
                    />
                  </div>

                  <div>
                    <Label>RG *</Label>
                    <Input
                      value={formData.rg}
                      onChange={(e) => setFormData({...formData, rg: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Acesso ao Sistema */}
              <div className="border-b border-[#27272A] pb-4">
                <h3 className="text-[#FFB800] font-semibold mb-3">Nivel Tecnico</h3>
                <div className="flex items-center gap-4">
                  <StarRating value={formData.nivel_tecnico} onChange={(v) => setFormData({...formData, nivel_tecnico: v})} size={24} />
                  <span className="text-zinc-400 text-sm">{formData.nivel_tecnico} estrela(s)</span>
                </div>
                <p className="text-zinc-500 text-xs mt-2">Define o nivel de qualificacao tecnica do prestador (1 a 5 estrelas).</p>
              </div>

              {/* Login */}
              <div className="border-b border-[#27272A] pb-4">
                <h3 className="text-[#FFB800] font-semibold mb-3">Acesso ao Sistema</h3>
                <p className="text-zinc-500 text-xs mb-3">Essas credenciais serao usadas pelo prestador para fazer login na plataforma.</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email (Login) *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      placeholder="email@prestador.com"
                      required
                      data-testid="prestador-login-email-input"
                    />
                  </div>
                  <div>
                    <Label>Senha *</Label>
                    <Input
                      type="text"
                      value={formData.senha_inicial}
                      onChange={(e) => setFormData({...formData, senha_inicial: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      placeholder="Minimo 6 caracteres"
                      required
                      minLength={6}
                      data-testid="prestador-login-password-input"
                    />
                  </div>
                </div>
              </div>

              {/* Endereço */}
              <div className="border-b border-[#27272A] pb-4">
                <h3 className="text-[#FFB800] font-semibold mb-3">Endereço</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>CEP *</Label>
                    <Input
                      value={formData.cep}
                      onChange={(e) => {
                        const cep = formatCEP(e.target.value);
                        setFormData({...formData, cep});
                        if (cep.replace(/\D/g, '').length === 8) {
                          buscarCEP(cep);
                        }
                      }}
                      className="bg-[#050505] border-[#27272A] text-white"
                      placeholder="00000-000"
                      maxLength={9}
                      required
                    />
                  </div>

                  <div className="col-span-2">
                    <Label>Logradouro *</Label>
                    <Input
                      value={formData.endereco}
                      onChange={(e) => setFormData({...formData, endereco: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      required
                    />
                  </div>

                  <div>
                    <Label>Número *</Label>
                    <Input
                      value={formData.numero}
                      onChange={(e) => setFormData({...formData, numero: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      required
                    />
                  </div>

                  <div>
                    <Label>Complemento</Label>
                    <Input
                      value={formData.complemento}
                      onChange={(e) => setFormData({...formData, complemento: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                    />
                  </div>

                  <div>
                    <Label>Bairro *</Label>
                    <Input
                      value={formData.bairro}
                      onChange={(e) => setFormData({...formData, bairro: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      required
                    />
                  </div>

                  <div>
                    <Label>Cidade *</Label>
                    <Input
                      value={formData.cidade}
                      onChange={(e) => setFormData({...formData, cidade: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      required
                    />
                  </div>

                  <div>
                    <Label>Estado *</Label>
                    <Input
                      value={formData.estado}
                      onChange={(e) => setFormData({...formData, estado: e.target.value.toUpperCase()})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      maxLength={2}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Telefones */}
              <div className="border-b border-[#27272A] pb-4">
                <h3 className="text-[#FFB800] font-semibold mb-3">Telefones</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Telefone Fixo</Label>
                    <Input
                      value={formData.telefone_fixo}
                      onChange={(e) => setFormData({...formData, telefone_fixo: formatPhone(e.target.value)})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      placeholder="(00) 0000-0000"
                    />
                  </div>

                  <div>
                    <Label>Telefone Celular *</Label>
                    <Input
                      value={formData.telefone_celular}
                      onChange={(e) => setFormData({...formData, telefone_celular: formatPhone(e.target.value)})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      placeholder="(00) 00000-0000"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Dados Profissionais */}
              <div className="border-b border-[#27272A] pb-4">
                <h3 className="text-[#FFB800] font-semibold mb-3">Dados Profissionais</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Escolaridade *</Label>
                    <Select value={formData.escolaridade} onValueChange={(value) => setFormData({...formData, escolaridade: value})}>
                      <SelectTrigger className="bg-[#050505] border-[#27272A] text-white">
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                        {ESCOLARIDADES.map(esc => (
                          <SelectItem key={esc} value={esc}>{esc}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Horário Disponível *</Label>
                    <Select value={formData.horario_disponivel} onValueChange={(value) => setFormData({...formData, horario_disponivel: value})}>
                      <SelectTrigger className="bg-[#050505] border-[#27272A] text-white">
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                        {HORARIOS.map(hor => (
                          <SelectItem key={hor} value={hor}>{hor}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="col-span-2">
                    <Label>Especialidades * (selecione ao menos uma)</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {ESPECIALIDADES.map(esp => (
                        <Badge
                          key={esp}
                          onClick={() => toggleEspecialidade(esp)}
                          className={`cursor-pointer ${
                            selectedEspecialidades.includes(esp)
                              ? 'bg-[#FFB800] text-black hover:bg-[#E6A600]'
                              : 'bg-[#1E1E1E] text-zinc-400 hover:bg-[#27272A]'
                          }`}
                        >
                          {esp}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Dados Financeiros */}
              <div>
                <h3 className="text-[#FFB800] font-semibold mb-3">Dados Financeiros</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Valor por Hora (R$) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.valor_hora}
                      onChange={(e) => setFormData({...formData, valor_hora: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      placeholder="0.00"
                      required
                    />
                  </div>

                  <div>
                    <Label>Taxa de Deslocamento (R$) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.taxa_deslocamento}
                      onChange={(e) => setFormData({...formData, taxa_deslocamento: e.target.value})}
                      className="bg-[#050505] border-[#27272A] text-white"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full bg-[#FFB800] text-black hover:bg-[#E6A600] py-3" data-testid="submit-prestador-button">
                Cadastrar Prestador
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Prestadores Table */}
      <div className="bg-[#121212] border border-[#27272A] rounded-sm overflow-x-auto">
        <table className="w-full text-sm text-left text-zinc-400">
          <thead className="text-xs uppercase bg-[#050505] text-zinc-400">
            <tr>
              <th className="px-6 py-3">Nome</th>
              <th className="px-6 py-3">CPF</th>
              <th className="px-6 py-3">Nivel</th>
              <th className="px-6 py-3">Especialidades</th>
              <th className="px-6 py-3">Valor/Hora</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3">Acoes</th>
            </tr>
          </thead>
          <tbody>
            {prestadores.map(prestador => (
              <tr key={prestador.id} className="border-b border-[#27272A] hover:bg-[#1E1E1E]" data-testid={`prestador-row-${prestador.cpf}`}>
                <td className="px-6 py-4 text-white font-medium">{prestador.name}</td>
                <td className="px-6 py-4">{prestador.cpf}</td>
                <td className="px-6 py-4">
                  <StarRating value={prestador.nivel_tecnico || 1} readOnly size={14} />
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {prestador.especialidades.slice(0, 2).map((esp, idx) => (
                      <span key={idx} className="text-xs bg-[#1E1E1E] px-2 py-1 rounded">{esp}</span>
                    ))}
                    {prestador.especialidades.length > 2 && (
                      <span className="text-xs text-[#FFB800]">+{prestador.especialidades.length - 2}</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-[#22C55E] font-semibold">R$ {prestador.valor_hora.toFixed(2)}</td>
                <td className="px-6 py-4">
                  <Badge className={`${
                    prestador.status === 'ativo' ? 'bg-[#22C55E]' :
                    prestador.status === 'pendente' ? 'bg-[#FFB800] text-black' : 'bg-[#EF4444]'
                  }`}>{prestador.status}</Badge>
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-2 items-center">
                    <button onClick={() => { setFinanceiroPrestador(prestador); setIsFinanceiroOpen(true); }} className="text-[#22C55E] hover:text-[#16A34A]" title="Financeiro" data-testid={`financeiro-prestador-${prestador.cpf}`}>
                      <CurrencyDollar size={18} />
                    </button>
                    {prestador.status !== 'ativo' && (
                      <button onClick={() => updateStatus(prestador.id, 'ativo')} className="text-[#22C55E] hover:text-[#16A34A]" title="Ativar">
                        <CheckCircle size={18} />
                      </button>
                    )}
                    {prestador.status !== 'inativo' && (
                      <button onClick={() => updateStatus(prestador.id, 'inativo')} className="text-[#EF4444] hover:text-[#DC2626]" title="Inativar">
                        <XCircle size={18} />
                      </button>
                    )}
                    <button onClick={() => openEditPrestador(prestador)} className="text-zinc-400 hover:text-[#FFB800] transition-colors" data-testid={`edit-prestador-${prestador.cpf}`}>
                      <PencilSimple size={16} />
                    </button>
                    <button onClick={() => { setSelectedPrestador(prestador); setIsDeleteOpen(true); }} className="text-zinc-400 hover:text-red-500 transition-colors" data-testid={`delete-prestador-${prestador.cpf}`}>
                      <Trash size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Credentials Dialog */}
      <Dialog open={isCredentialsDialogOpen} onOpenChange={setIsCredentialsDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white" data-testid="prestador-credentials-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#22C55E]">Prestador Criado com Sucesso!</DialogTitle>
          </DialogHeader>
          {createdCredentials && (
            <div className="space-y-4">
              <p className="text-zinc-400 text-sm">Anote as credenciais de acesso do prestador:</p>
              <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm space-y-3">
                <div>
                  <div className="text-zinc-400 text-xs">Nome</div>
                  <div className="text-white font-medium">{createdCredentials.nome}</div>
                </div>
                <div>
                  <div className="text-zinc-400 text-xs">Email de Acesso</div>
                  <div className="text-[#FFB800] font-mono" data-testid="prestador-created-email">{createdCredentials.email}</div>
                </div>
                <div>
                  <div className="text-zinc-400 text-xs">Senha</div>
                  <div className="text-[#FFB800] font-mono" data-testid="prestador-created-password">{createdCredentials.senha}</div>
                </div>
              </div>
              <p className="text-zinc-500 text-xs">Compartilhe essas credenciais com o prestador para que ele possa acessar o sistema.</p>
              <Button onClick={() => setIsCredentialsDialogOpen(false)} className="w-full bg-[#FFB800] text-black hover:bg-[#E6A600]">Entendido</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Prestador Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Barlow Condensed' }}>Editar Prestador</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditPrestador} className="space-y-6">
            {/* Dados Pessoais */}
            <div className="border-b border-[#27272A] pb-4">
              <h3 className="text-[#FFB800] font-semibold mb-3">Dados Pessoais</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Nome Completo *</Label>
                  <Input value={editData.name || ''} onChange={(e) => setEditData({...editData, name: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required data-testid="edit-prestador-name" />
                </div>
                <div>
                  <Label>CPF *</Label>
                  <Input value={editData.cpf || ''} onChange={(e) => setEditData({...editData, cpf: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required />
                </div>
                <div>
                  <Label>RG *</Label>
                  <Input value={editData.rg || ''} onChange={(e) => setEditData({...editData, rg: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required />
                </div>
              </div>
            </div>

            {/* Email (read-only) */}
            <div className="border-b border-[#27272A] pb-4">
              <h3 className="text-[#FFB800] font-semibold mb-3">Acesso ao Sistema</h3>
              <div>
                <Label>Email (Login)</Label>
                <Input type="email" value={editData.email || ''} className="bg-[#050505] border-[#27272A] text-zinc-500" disabled />
                <p className="text-zinc-500 text-xs mt-1">O email de login nao pode ser alterado.</p>
              </div>
            </div>

            {/* Endereco */}
            <div className="border-b border-[#27272A] pb-4">
              <h3 className="text-[#FFB800] font-semibold mb-3">Endereco</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>CEP *</Label>
                  <Input value={editData.cep || ''} onChange={(e) => setEditData({...editData, cep: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" placeholder="00000-000" required />
                </div>
                <div className="col-span-2">
                  <Label>Endereco *</Label>
                  <Input value={editData.endereco || ''} onChange={(e) => setEditData({...editData, endereco: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required />
                </div>
                <div>
                  <Label>Numero *</Label>
                  <Input value={editData.numero || ''} onChange={(e) => setEditData({...editData, numero: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required />
                </div>
                <div>
                  <Label>Complemento</Label>
                  <Input value={editData.complemento || ''} onChange={(e) => setEditData({...editData, complemento: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" />
                </div>
                <div>
                  <Label>Bairro *</Label>
                  <Input value={editData.bairro || ''} onChange={(e) => setEditData({...editData, bairro: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required />
                </div>
                <div>
                  <Label>Cidade *</Label>
                  <Input value={editData.cidade || ''} onChange={(e) => setEditData({...editData, cidade: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required />
                </div>
                <div>
                  <Label>Estado *</Label>
                  <Input value={editData.estado || ''} onChange={(e) => setEditData({...editData, estado: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" maxLength={2} required />
                </div>
              </div>
            </div>

            {/* Contato */}
            <div className="border-b border-[#27272A] pb-4">
              <h3 className="text-[#FFB800] font-semibold mb-3">Contato</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Telefone Fixo</Label>
                  <Input value={editData.telefone_fixo || ''} onChange={(e) => setEditData({...editData, telefone_fixo: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" />
                </div>
                <div>
                  <Label>Telefone Celular *</Label>
                  <Input value={editData.telefone_celular || ''} onChange={(e) => setEditData({...editData, telefone_celular: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required />
                </div>
              </div>
            </div>

            {/* Formacao e Disponibilidade */}
            <div className="border-b border-[#27272A] pb-4">
              <h3 className="text-[#FFB800] font-semibold mb-3">Formacao e Disponibilidade</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Escolaridade *</Label>
                  <Select value={editData.escolaridade || '_none'} onValueChange={(v) => setEditData({...editData, escolaridade: v === '_none' ? '' : v})}>
                    <SelectTrigger className="bg-[#050505] border-[#27272A] text-white"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                      {ESCOLARIDADES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Horario Disponivel *</Label>
                  <Select value={editData.horario_disponivel || '_none'} onValueChange={(v) => setEditData({...editData, horario_disponivel: v === '_none' ? '' : v})}>
                    <SelectTrigger className="bg-[#050505] border-[#27272A] text-white"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                      {HORARIOS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Especialidades */}
            <div className="border-b border-[#27272A] pb-4">
              <h3 className="text-[#FFB800] font-semibold mb-3">Especialidades *</h3>
              <div className="flex flex-wrap gap-2">
                {ESPECIALIDADES.map(esp => (
                  <Badge
                    key={esp}
                    className={`cursor-pointer transition-colors ${
                      editData.especialidades?.includes(esp) ? 'bg-[#FFB800] text-black hover:bg-[#E6A600]' : 'bg-[#1E1E1E] text-zinc-400 hover:bg-[#27272A]'
                    }`}
                    onClick={() => {
                      const current = editData.especialidades || [];
                      const updated = current.includes(esp) ? current.filter(e => e !== esp) : [...current, esp];
                      setEditData({...editData, especialidades: updated});
                    }}
                  >
                    {esp}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Dados Financeiros */}
            <div className="border-b border-[#27272A] pb-4">
              <h3 className="text-[#FFB800] font-semibold mb-3">Dados Financeiros</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Valor por Hora (R$) *</Label>
                  <Input type="number" step="0.01" value={editData.valor_hora || ''} onChange={(e) => setEditData({...editData, valor_hora: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" placeholder="0.00" required />
                </div>
                <div>
                  <Label>Taxa de Deslocamento (R$) *</Label>
                  <Input type="number" step="0.01" value={editData.taxa_deslocamento || ''} onChange={(e) => setEditData({...editData, taxa_deslocamento: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" placeholder="0.00" required />
                </div>
              </div>
            </div>

            {/* Nivel Tecnico */}
            <div>
              <h3 className="text-[#FFB800] font-semibold mb-3">Nivel Tecnico</h3>
              <div className="flex items-center gap-4">
                <StarRating value={editData.nivel_tecnico || 1} onChange={(v) => setEditData({...editData, nivel_tecnico: v})} size={24} />
                <span className="text-zinc-400 text-sm">{editData.nivel_tecnico || 1} estrela(s)</span>
              </div>
              <p className="text-zinc-500 text-xs mt-2">Define o nivel de qualificacao tecnica do prestador.</p>
            </div>

            {/* Documentos */}
            {selectedPrestador && (
              <div className="border-t border-[#27272A] pt-4">
                <DocumentUploader
                  entityId={selectedPrestador.id}
                  entityType="prestadores"
                  documents={selectedPrestador.documentos_urls || []}
                  onUpdate={() => fetchPrestadores()}
                />
              </div>
            )}

            <Button type="submit" className="w-full bg-[#FFB800] text-black hover:bg-[#E6A600] py-3" data-testid="submit-edit-prestador">
              Salvar Alteracoes
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Prestador Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Excluir Prestador</DialogTitle>
          </DialogHeader>
          {selectedPrestador && (
            <div className="space-y-4">
              <p className="text-zinc-400">Tem certeza que deseja excluir o prestador <strong className="text-white">{selectedPrestador.name}</strong>?</p>
              <p className="text-red-400 text-xs">Esta acao nao pode ser desfeita.</p>
              <div className="flex gap-3">
                <Button onClick={() => setIsDeleteOpen(false)} className="flex-1 bg-[#1E1E1E] hover:bg-[#27272A]">Cancelar</Button>
                <Button onClick={handleDeletePrestador} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="confirm-delete-prestador">Excluir</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Financeiro Prestador */}
      {financeiroPrestador && (
        <PrestadorFinanceiro
          prestador={financeiroPrestador}
          isOpen={isFinanceiroOpen}
          onClose={() => { setIsFinanceiroOpen(false); setFinanceiroPrestador(null); }}
        />
      )}
    </div>
  );
};

export default PrestadorManagement;
